import { apiClient } from "./client";

export type DocumentKind =
  | "DIPLOMA"
  | "RPPS_ATTEST"
  | "DPC_CERT"
  | "LIABILITY_INSURANCE"
  | "MEDICAL_DEGREE"
  | "ID_CARD"
  | "OTHER";

export type PractitionerDocument = {
  id: string;
  practitioner_id: string;
  kind: DocumentKind;
  label: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  issued_at: string | null;
  expires_at: string | null;
  uploaded_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  is_expired: boolean;
  days_until_expiry: number | null;
};

export type UploadDocumentPayload = {
  file: File;
  kind: DocumentKind;
  label: string;
  issued_at?: string;
  expires_at?: string;
};

export async function listMyDocuments(): Promise<PractitionerDocument[]> {
  const { data } = await apiClient.get<PractitionerDocument[]>("/api/v1/me/documents");
  return data;
}

export async function uploadMyDocument(
  p: UploadDocumentPayload,
): Promise<PractitionerDocument> {
  const form = new FormData();
  form.append("file", p.file);
  form.append("kind", p.kind);
  form.append("label", p.label);
  if (p.issued_at) form.append("issued_at", p.issued_at);
  if (p.expires_at) form.append("expires_at", p.expires_at);
  const { data } = await apiClient.post<PractitionerDocument>(
    "/api/v1/me/documents",
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function downloadMyDocument(
  id: string,
): Promise<{ blob: Blob; filename: string }> {
  const resp = await apiClient.get<Blob>(`/api/v1/me/documents/${id}/download`, {
    responseType: "blob",
  });
  const dispo = (resp.headers["content-disposition"] as string | undefined) ?? "";
  const match = /filename="([^"]+)"/.exec(dispo);
  const filename = match?.[1] ?? "document";
  return { blob: resp.data, filename };
}

export async function deleteMyDocument(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/me/documents/${id}`);
}

// ----------------------------------------------------------------------------
// Admin / compliance surface
// ----------------------------------------------------------------------------
export type ComplianceRow = {
  practitioner_id: string;
  last_name: string;
  first_initial: string | null;
  rpps: string | null;
  counts_by_kind: Record<string, number>;
  expired_count: number;
  next_expiry: string | null;
};

export async function listComplianceSummary(
  expiringWithinDays = 90,
): Promise<ComplianceRow[]> {
  const { data } = await apiClient.get<ComplianceRow[]>(
    "/api/v1/practitioners/documents/compliance",
    { params: { expiring_within_days: expiringWithinDays } },
  );
  return data;
}

export async function listPractitionerDocuments(
  practitionerId: string,
): Promise<PractitionerDocument[]> {
  const { data } = await apiClient.get<PractitionerDocument[]>(
    `/api/v1/practitioners/${practitionerId}/documents`,
  );
  return data;
}

export async function downloadPractitionerDocument(
  practitionerId: string,
  docId: string,
): Promise<{ blob: Blob; filename: string }> {
  const resp = await apiClient.get<Blob>(
    `/api/v1/practitioners/${practitionerId}/documents/${docId}/download`,
    { responseType: "blob" },
  );
  const dispo = (resp.headers["content-disposition"] as string | undefined) ?? "";
  const match = /filename="([^"]+)"/.exec(dispo);
  const filename = match?.[1] ?? "document";
  return { blob: resp.data, filename };
}

export async function adminDeletePractitionerDocument(
  practitionerId: string,
  docId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/practitioners/${practitionerId}/documents/${docId}`,
  );
}
